import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Shield, Flame, Plus, Trash2, AlertCircle, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useSecurityContext } from '@/contexts/MaintenanceContext';
import { getSystemCategory, getSystemTypeLabel } from '@/lib/systemTypeConstants';
import { usePlantillasContratos, type TipoContrato } from '@/hooks/usePlantillasContratos';
import {
  useContratosMantenimiento,
  calcularPrecioPeriodo,
} from '@/hooks/useContratosMantenimiento';
import { generarContratoPdf } from '@/lib/contratos/contratoPdf';
import {
  sanitizarTextoContrato,
  CLAUSULA_CRA,
  TITULO_CLAUSULA_CRA,
  type ClausulaContrato,
} from '@/lib/contratos/plantillasPorDefecto';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  clienteId: string;
  onCreated?: () => void;
}

export function ContratoFormDialog({ open, onOpenChange, clienteId, onCreated }: Props) {
  const { sistemas, clientes, sedes } = useSecurityContext();
  const { getPlantilla } = usePlantillasContratos();
  const { crearContrato, subirPdf } = useContratosMantenimiento(clienteId);

  const cliente = clientes.find(c => c.id === clienteId);

  const [tipo, setTipo] = useState<TipoContrato>('seguridad');
  const [tab, setTab] = useState('datos');
  const [fechaInicio, setFechaInicio] = useState(new Date().toISOString().slice(0, 10));
  const [duracionMeses, setDuracionMeses] = useState(12);
  const [renovacion, setRenovacion] = useState(true);
  const [periodicidad, setPeriodicidad] = useState<'anual' | 'semestral' | 'trimestral' | 'mensual'>('anual');
  const [precioTotal, setPrecioTotal] = useState(0);
  const [ivaIncluido, setIvaIncluido] = useState(false);
  const [sistemasSel, setSistemasSel] = useState<Set<string>>(new Set());
  const [objeto, setObjeto] = useState('');
  const [clausulas, setClausulas] = useState<ClausulaContrato[]>([]);
  const [textoLegal, setTextoLegal] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [firmante, setFirmante] = useState('');
  const [generando, setGenerando] = useState(false);

  // Carga inicial de plantilla cuando se cambia el tipo o se abre
  useEffect(() => {
    if (!open) return;
    const p = getPlantilla(tipo);
    setObjeto(p.objeto);
    setClausulas(p.clausulas.map(c => ({ ...c })));
    setTextoLegal(p.texto_legal);
    setSistemasSel(new Set());
  }, [tipo, open, getPlantilla]);

  // Sincronizar cláusula CRA automáticamente según selección de sistemas
  useEffect(() => {
    const hayIntrusion = Array.from(sistemasSel).some(id => {
      const s = sistemas.find(x => x.id === id);
      return s?.tipo === 'intrusión';
    });
    setClausulas(prev => {
      const idx = prev.findIndex(c => c.titulo === TITULO_CLAUSULA_CRA);
      if (hayIntrusion && idx === -1) {
        return [...prev, { ...CLAUSULA_CRA }];
      }
      if (!hayIntrusion && idx !== -1) {
        return prev.filter((_, i) => i !== idx);
      }
      return prev;
    });
  }, [sistemasSel, sistemas]);

  useEffect(() => {
    if (open) {
      setTab('datos');
      setFechaInicio(new Date().toISOString().slice(0, 10));
      setDuracionMeses(12);
      setRenovacion(true);
      setPeriodicidad('anual');
      setPrecioTotal(0);
      setIvaIncluido(false);
      setObservaciones('');
      setFirmante('');
    }
  }, [open]);

  const sistemasCliente = useMemo(
    () => sistemas.filter(s => s.cliente_id === clienteId && getSystemCategory(s.tipo) === tipo),
    [sistemas, clienteId, tipo],
  );

  const precioPeriodo = calcularPrecioPeriodo(precioTotal, periodicidad);

  const toggleSistema = (id: string) => {
    setSistemasSel(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const addClausula = () => setClausulas(prev => [...prev, { titulo: '', contenido: '' }]);
  const removeClausula = (i: number) => setClausulas(prev => prev.filter((_, idx) => idx !== i));
  const updateClausula = (i: number, patch: Partial<ClausulaContrato>) =>
    setClausulas(prev => prev.map((c, idx) => idx === i ? { ...c, ...patch } : c));

  const guardar = async (estadoFinal: 'borrador' | 'vigente') => {
    if (!cliente) return;
    if (precioTotal < 0) {
      toast.error('El precio no puede ser negativo');
      return;
    }
    if (sistemasSel.size === 0) {
      toast.error('Debe seleccionar al menos un sistema para incluir en el contrato');
      setTab('alcance');
      return;
    }
    setGenerando(true);
    try {
      const clausulasLimpias = clausulas
        .map(c => ({
          titulo: sanitizarTextoContrato(c.titulo),
          contenido: sanitizarTextoContrato(c.contenido),
        }))
        .filter(c => c.titulo || c.contenido);

      const hayIntrusionGuardar = Array.from(sistemasSel).some(id => {
        const s = sistemas.find(x => x.id === id);
        return s?.tipo === 'intrusión';
      });
      if (hayIntrusionGuardar && !clausulasLimpias.some(c => c.titulo === TITULO_CLAUSULA_CRA)) {
        clausulasLimpias.push({
          titulo: sanitizarTextoContrato(CLAUSULA_CRA.titulo),
          contenido: sanitizarTextoContrato(CLAUSULA_CRA.contenido),
        });
      }

      const contrato = await crearContrato({
        cliente_id: clienteId,
        tipo,
        fecha_inicio: fechaInicio,
        duracion_meses: duracionMeses,
        renovacion_automatica: renovacion,
        periodicidad_pago: periodicidad,
        precio_total: precioTotal,
        iva_incluido: ivaIncluido,
        sistemas_ids: Array.from(sistemasSel),
        objeto: sanitizarTextoContrato(objeto),
        clausulas: clausulasLimpias,
        texto_legal: sanitizarTextoContrato(textoLegal),
        observaciones: sanitizarTextoContrato(observaciones),
        estado: estadoFinal,
        nombre_firmante_cliente: firmante || undefined,
      });

      // Generar PDF y subir
      const sistemasInfo = Array.from(sistemasSel).map(id => {
        const s = sistemas.find(x => x.id === id);
        const sede = s?.sede_id ? sedes.find(se => se.id === s.sede_id) : null;
        const direccionPartes = sede ? [sede.direccion, sede.codigo_postal, sede.poblacion, sede.provincia].filter(Boolean).join(', ') : '';
        return {
          tipo: s?.tipo || '',
          tipoLabel: s ? getSystemTypeLabel(s.tipo) : '',
          sedeNombre: sede?.nombre || undefined,
          sedeDireccion: direccionPartes || null,
        };
      });

      const blob = await generarContratoPdf({
        empresaId: cliente.empresa_id!,
        cliente,
        contrato: {
          numero_formateado: contrato.numero_formateado,
          tipo: contrato.tipo,
          fecha_inicio: contrato.fecha_inicio,
          fecha_fin: contrato.fecha_fin,
          duracion_meses: contrato.duracion_meses,
          renovacion_automatica: contrato.renovacion_automatica,
          periodicidad_pago: contrato.periodicidad_pago,
          precio_total: contrato.precio_total,
          precio_periodo: contrato.precio_periodo,
          iva_incluido: contrato.iva_incluido,
          objeto: contrato.objeto,
          clausulas: contrato.clausulas,
          texto_legal: contrato.texto_legal,
          observaciones: contrato.observaciones,
          nombre_firmante_cliente: contrato.nombre_firmante_cliente,
        },
        sistemas: sistemasInfo,
        esBorrador: estadoFinal === 'borrador',
      });

      await subirPdf(contrato, blob);

      toast.success(estadoFinal === 'vigente' ? 'Contrato creado y firmado' : 'Borrador guardado');
      if (estadoFinal === 'vigente' && sistemasSel.size > 0) {
        toast.info('Cobertura de revisiones activada para los sistemas incluidos');
      }
      onCreated?.();
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message || 'Error al crear el contrato');
    } finally {
      setGenerando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {tipo === 'seguridad' ? <Shield className="h-5 w-5" /> : <Flame className="h-5 w-5" />}
            Nuevo contrato de mantenimiento
          </DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="datos">1. Datos</TabsTrigger>
            <TabsTrigger value="alcance">2. Alcance</TabsTrigger>
            <TabsTrigger value="clausulas">3. Cláusulas</TabsTrigger>
          </TabsList>

          {/* Paso 1 */}
          <TabsContent value="datos" className="space-y-4 pt-4">
            <div>
              <Label>Tipo de contrato</Label>
              <Select value={tipo} onValueChange={(v) => setTipo(v as TipoContrato)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="seguridad">Seguridad (Intrusión / CCTV / Accesos)</SelectItem>
                  <SelectItem value="pci">Protección Contra Incendios (PCI)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Fecha de inicio</Label>
                <Input type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} />
              </div>
              <div>
                <Label>Duración (meses)</Label>
                <Input type="number" min={1} value={duracionMeses} onChange={e => setDuracionMeses(Number(e.target.value) || 12)} />
              </div>
            </div>

            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <Label className="cursor-pointer">Renovación automática</Label>
                <p className="text-xs text-muted-foreground">Tácitamente por períodos iguales salvo denuncia</p>
              </div>
              <Switch checked={renovacion} onCheckedChange={setRenovacion} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Periodicidad de pago</Label>
                <Select value={periodicidad} onValueChange={(v) => setPeriodicidad(v as any)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="anual">Anual</SelectItem>
                    <SelectItem value="semestral">Semestral</SelectItem>
                    <SelectItem value="trimestral">Trimestral</SelectItem>
                    <SelectItem value="mensual">Mensual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Precio anual (€)</Label>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={precioTotal}
                  onChange={e => setPrecioTotal(Number(e.target.value) || 0)}
                />
              </div>
            </div>

            <div className="rounded-md bg-muted/50 p-3 text-sm flex items-center justify-between">
              <span>Cuota {periodicidad}</span>
              <span className="font-semibold">{precioPeriodo.toFixed(2)} €</span>
            </div>

            <div className="flex items-center justify-between rounded-md border p-3">
              <Label className="cursor-pointer">IVA incluido en el precio</Label>
              <Switch checked={ivaIncluido} onCheckedChange={setIvaIncluido} />
            </div>
          </TabsContent>

          {/* Paso 2 */}
          <TabsContent value="alcance" className="space-y-3 pt-4">
            <div className="flex items-start gap-2 rounded-md bg-primary/5 border border-primary/20 p-3 text-sm">
              <ShieldCheck className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <p>
                Los sistemas seleccionados <strong>activarán automáticamente su control de cobertura de revisiones</strong> al firmar el contrato (estado "vigente"). <strong className="text-destructive">Es obligatorio seleccionar al menos un sistema.</strong>
              </p>
            </div>
            {sistemasCliente.length === 0 ? (
              <div className="flex items-center gap-2 text-destructive py-8 justify-center">
                <AlertCircle className="h-4 w-4" />
                <span>No hay sistemas de {tipo} dados de alta para este cliente. Dé de alta un sistema antes de crear el contrato.</span>
              </div>
            ) : (
              <div className="space-y-2">
                {sistemasCliente.map(s => (
                  <label
                    key={s.id}
                    className={`flex items-center gap-3 rounded-md border p-3 cursor-pointer hover:bg-muted/40 ${sistemasSel.has(s.id) ? 'border-primary bg-primary/5' : ''}`}
                  >
                    <Checkbox
                      checked={sistemasSel.has(s.id)}
                      onCheckedChange={() => toggleSistema(s.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{getSystemTypeLabel(s.tipo)}</span>
                        <Badge variant="outline" className="text-xs">{tipo.toUpperCase()}</Badge>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
            {sistemasSel.size === 0 ? (
              <div className="flex items-center gap-2 text-destructive text-sm font-medium">
                <AlertCircle className="h-4 w-4" />
                <span>No ha seleccionado ningún sistema. Seleccione al menos uno para continuar.</span>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground pt-2">
                {sistemasSel.size} sistema(s) seleccionado(s)
              </p>
            )}
          </TabsContent>

          {/* Paso 3 */}
          <TabsContent value="clausulas" className="space-y-4 pt-4">
            <div>
              <Label>Objeto del contrato</Label>
              <Textarea
                value={objeto}
                onChange={e => setObjeto(e.target.value)}
                rows={3}
                placeholder="Descripción del objeto del contrato"
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Cláusulas (texto plano)</Label>
                <Button type="button" size="sm" variant="outline" onClick={addClausula}>
                  <Plus className="h-3.5 w-3.5 mr-1" /> Añadir
                </Button>
              </div>
              {clausulas.map((c, i) => (
                <div key={i} className="rounded-md border p-3 space-y-2">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Título"
                      value={c.titulo}
                      onChange={e => updateClausula(i, { titulo: e.target.value })}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeClausula(i)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <Textarea
                    placeholder="Contenido (sin HTML — saltos de línea con Enter)"
                    value={c.contenido}
                    onChange={e => updateClausula(i, { contenido: e.target.value })}
                    rows={5}
                  />
                </div>
              ))}
            </div>

            <div>
              <Label>Observaciones</Label>
              <Textarea value={observaciones} onChange={e => setObservaciones(e.target.value)} rows={2} />
            </div>

            <div>
              <Label>Nombre del firmante por el cliente (opcional)</Label>
              <Input value={firmante} onChange={e => setFirmante(e.target.value)} placeholder="Persona que firma" />
            </div>
          </TabsContent>
        </Tabs>

        {sistemasSel.size === 0 && (
          <div className="flex items-center gap-2 text-destructive text-sm px-1">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Seleccione al menos un sistema en <strong>Alcance</strong> para poder generar el contrato.</span>
          </div>
        )}
        <div className="rounded-md border bg-muted/30 p-3 text-xs space-y-1.5">
          <p className="font-semibold text-foreground">¿Qué hace cada botón?</p>
          <p>
            <strong>Guardar borrador:</strong> guarda el contrato como BORRADOR. El PDF se generará con marca de agua y una nota indicando que <strong>no es vigente</strong>. Los sistemas incluidos <strong>NO activan</strong> la cobertura de mantenimiento ni la planificación de revisiones.
          </p>
          <p>
            <strong>Guardar y firmar:</strong> guarda el contrato como VIGENTE. Activa la <strong>cobertura de mantenimiento</strong> y la planificación de revisiones para los sistemas incluidos.
          </p>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={generando}>
            Cancelar
          </Button>
          <Button variant="outline" onClick={() => guardar('borrador')} disabled={generando || sistemasSel.size === 0}>
            Guardar borrador
          </Button>
          <Button onClick={() => guardar('vigente')} disabled={generando || sistemasSel.size === 0}>
            {generando ? 'Generando…' : 'Guardar y firmar'}
          </Button>
        </DialogFooter>

      </DialogContent>
    </Dialog>
  );
}
